import Config from '@config';
import { normalizeInfiniteQueryData } from '@gasbi/shared/common/features/utils';
import { createApi } from '@reduxjs/toolkit/query/react';

import { CACHE_EXPIRATION_TIME } from '@/features/constants';
import { FiltersGrid, Pagination } from '@/features/types';
import { infiniteQueryOptions } from '@/features/utils';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

import {
  CommercialMeteringPointLastOwnership,
  CommercialMeteringPointOwnership,
  CommercialMeteringPointOwnershipGrid,
  CommercialMeteringPointOwnershipRequestBody,
} from './types';

export const commercialMeteringPointOwnershipsApi = createApi({
  reducerPath: 'commercialMeteringPointOwnershipsApi',
  baseQuery: axiosBaseQuery({
    baseUrl: Config.VITE_API_URL + '/commercial-metering-point-ownerships',
  }),
  tagTypes: [
    'CommercialMeteringPointOwnership',
    'CommercialMeteringPointOwnerships',
  ],
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  endpoints: builder => ({
    getCommercialMeteringPointOwnershipsI: builder.infiniteQuery<
      CommercialMeteringPointOwnershipGrid,
      FiltersGrid,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: `/query`,
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: [
        'CommercialMeteringPointOwnership',
        'CommercialMeteringPointOwnerships',
      ],
    }),
    getCommercialMeteringPointOwnershipLastOwnership: builder.query<
      CommercialMeteringPointLastOwnership,
      {
        commercialMeteringPointId?: string;
        date?: string;
        dateTime?: string;
      }
    >({
      query: params => ({
        url: '/last-ownership',
        params,
      }),
    }),
    getCommercialMeteringPointOwnerships: builder.query<
      CommercialMeteringPointOwnershipGrid[],
      FiltersGrid
    >({
      query: params => ({
        url: '/query',
        params,
      }),
      providesTags: [
        'CommercialMeteringPointOwnership',
        'CommercialMeteringPointOwnerships',
      ],
    }),
    getCommercialMeteringPointOwnershipById: builder.query<
      CommercialMeteringPointOwnership,
      string
    >({
      query: id => ({
        url: `/${id}`,
      }),
      providesTags: ['CommercialMeteringPointOwnership'],
    }),
    createCommercialMeteringPointOwnership: builder.mutation<
      CommercialMeteringPointOwnership,
      CommercialMeteringPointOwnershipRequestBody
    >({
      query: data => ({
        url: '/',
        method: 'POST',
        data,
      }),
      invalidatesTags: result => [
        'CommercialMeteringPointOwnership',
        { type: 'CommercialMeteringPointOwnership', id: result?.id },
      ],
    }),
    updateCommercialMeteringPointOwnership: builder.mutation<
      CommercialMeteringPointOwnership,
      CommercialMeteringPointOwnershipRequestBody
    >({
      query: data => ({
        url: `/${data.id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: result => [
        'CommercialMeteringPointOwnership',
        { type: 'CommercialMeteringPointOwnership', id: result?.id },
      ],
    }),
    deleteCommercialMeteringPointOwnership: builder.mutation<
      CommercialMeteringPointOwnership,
      string
    >({
      query: id => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, id) => [
        'CommercialMeteringPointOwnership',
        { type: 'CommercialMeteringPointOwnership', id },
      ],
    }),
  }),
});

export const {
  useGetCommercialMeteringPointOwnershipsQuery,
  useGetCommercialMeteringPointOwnershipByIdQuery,
  useCreateCommercialMeteringPointOwnershipMutation,
  useUpdateCommercialMeteringPointOwnershipMutation,
  useDeleteCommercialMeteringPointOwnershipMutation,
  useGetCommercialMeteringPointOwnershipLastOwnershipQuery,
} = commercialMeteringPointOwnershipsApi;

export const useGetCommercialMeteringPointOwnershipsInfiniteQuery =
  normalizeInfiniteQueryData(
    commercialMeteringPointOwnershipsApi.useGetCommercialMeteringPointOwnershipsIInfiniteQuery,
  );
